import { useContext, useMemo } from 'react'
import { ScenarioStatesProviderContext } from '../providers/StatesProvider'
import MissionService from '../../../../service/MissionService'
import GroundStationService from '../../../../service/GroundStationService'
import SimulateService from '../../../../service/SimulateService'
import { getApiFailureMessage, getSatelliteColor, showToastMessage } from '../../../../utils/commonFunctions'
import { getDefaultHWILConfig } from '../../utils'
import { SCENARIO_DEPLOYMENT_TYPES, SCENARIO_OPERATION_TYPES, SCENARIO_STATUS, SCENARIO_STEPS } from '../../constants'
import useHwilApi from './useHwilApi'
import {
    AllHwilComponentType,
    FaultsOptionType,
    FaultSubsystemsType,
    FormAssetType,
    FormGroupDetailsType,
    GroupOptionType,
    ScenarioFormDataType,
    SelectedFaultType,
    SelectedTaskplansType,
    TaskPlanOptionType
} from '../utils/scenario-types'
import { RemoteAppDataType, ScenarioResponseType } from '../../types'
import { formatOrbitalInsightsData, formatPresimData, getFormattedScheduleData, getValidEpoch, getViewerPacketDetails } from '../utils/helperFunction'
import useSelectedGroupSatellite from './useSelectedGroupSatellite'
import usePresimApis from './usePresimApis'
import { createGroundPacket, createSatellitePacket } from '../utils/viewer-packets'
import useFaultApis from './useFaultApis'
import { getOrbitalInsights } from '../../../CommonComponents/orbital-insights/api'
import useNavigation from './useNavigation'
import useTimelineFilter from './useTimelineFilter'
import {
    getScenarioSetupMessage,
    getScenarioSetupStatus,
    isSatelliteGroupsAddRunning,
    isOnboardScheduleGenerateRunning,
    SCENARIO_SETUP_STATUS
} from '../utils/satellite-groups-add'
import useStepper from './useStepper'

const useBaseApi = () => {
    const { type } = useNavigation()
    const context = useContext(ScenarioStatesProviderContext)
    if (context === undefined) {
        throw new Error('useBaseApi must be used within a Scenario States Provider')
    }
    const { getPresimData } = usePresimApis()
    const { resetTimelineFilter } = useTimelineFilter()
    const { getallFaultReportData, getFaultsByGroupId } = useFaultApis()
    const { getHWILComponents, getRemoteAppConnectionStatus } = useHwilApi()
    const { onSelectGroup, selectedGroupSatellite } = useSelectedGroupSatellite()
    const { viewer, formData, setFormData, setScenarioBaseData, setLoading, setScenarioResponse, setSatelliteBaseDataLoaded } = context
    const { updateScenarioStep } = useStepper()
    const scenarioStartTime = useMemo(() => formData.details.scenarioStartTime, [formData.details.scenarioStartTime]);

    const getAllPayloadsDetails = async (missionId: string): Promise<Record<string, RemoteAppDataType[]>> => {

        try {
            const response = await MissionService.getMissionSatInfo(missionId);
            const groups = response?.data?.satelliteGroup || [];
            const allPayloads = groups.map(group => ({
                groupId: group?.satelliteGroup?.uniqueId,
                payloads: (group?.payloads || []).map(payload => {
                    const details = {
                        id: payload?.uniqueId as string,
                        appId: Number(
                            group?.satelliteGroup?.satConfig?.payload
                                ?.find(mp => mp?.payloadId === payload?.uniqueId)
                                ?.appId ?? 0
                        ),
                        name: payload?.name as string,
                        isConfigured: "Yes" as "Yes",
                        payloadType:
                            payload?.atmosData?.component_specific_attributes?.payloadType,
                        payloadSubType:
                            payload?.atmosData?.component_specific_attributes?.payloadSubType,
                        payloadHWName: payload?.atmosData?.name,
                        interfaces: payload?.userData?.selected_interfaces || [],
                    };

                    return {
                        ...details,
                        ...getDefaultHWILConfig(
                            JSON.stringify(details),
                            JSON.stringify({})
                        ),
                    };
                }),
            }));

            const validResults = allPayloads.filter(r => r?.groupId);
            return validResults.reduce((acc, { groupId, payloads }) => {
                acc[groupId] = payloads;
                return acc;
            }, {} as Record<string, RemoteAppDataType[]>);

        } catch (error) {
            return {};
        }
    };


    const getAllGroundStations = (mission_gs_list: string[]): Promise<any> => {
        return new Promise((resolve) => {
            GroundStationService.getGroundStationsByMissionId()
                .then((res) => {
                    if (res.data) {
                        const gs_list = res?.['data'].filter(gs =>
                            mission_gs_list.includes(gs?.['groundStationId'])
                        );
                        resolve(gs_list);
                    } else {
                        resolve([]);
                    }
                })
                .catch((err) => {
                    resolve([]);
                });
        });
    };

    const getMissionDetails = (missionId: string): Promise<any> => {
        return new Promise((resolve) => {
            MissionService.get_mission_by_id(missionId)
                .then((res) => {
                    if (res?.data?.['gsList']?.length > 0) {
                        const gs_list = res.data['gsList'];
                        return getAllGroundStations(gs_list);
                    }
                    return [];
                })
                .then((groundStations) => {
                    resolve(groundStations);
                })
                .catch((err) => {
                    resolve([]);
                });
        });
    };

    const getAllGeoFeatures = (missionId: string): Promise<any> => {
        return new Promise((resolve) => {
            MissionService.getAllAoisByMissionId(missionId)
                .then((res) => {
                    if (res?.data?.length > 0) {
                        resolve(res.data);
                    } else {
                        resolve([]);
                    }
                })
                .catch((err) => {
                    resolve([]);
                });
        });
    };

    const getTaskPlansByGroupId = (groupId: string): Promise<TaskPlanOptionType[]> => {
        return new Promise((resolve, reject) => {
            MissionService.getAllTaskPlan(groupId)
                .then((response) => {
                    if (response?.data && response.data.length > 0) {
                        resolve(response.data);
                    } else {
                        resolve([]);
                    }
                })
                .catch((error) => {
                    reject(new Error('Failed to fetch task plans: ' + error));
                });
        });
    }

    const fetchTaskPlansForGroups = async (satelliteGroups: string[]): Promise<Record<string, TaskPlanOptionType[]>> => {
        if (!satelliteGroups?.length) return {};

        const groupPromises = satelliteGroups.map(async (groupId) => {
            try {
                const groupTaskPlans = await getTaskPlansByGroupId(groupId);
                return { groupId: groupId, taskPlans: groupTaskPlans };
            } catch (err) {
                return { groupId: groupId, taskPlans: [] };
            }
        });

        const results = await Promise.all(groupPromises);
        const validResults = results.filter(r => r !== null);

        return validResults.reduce((acc, { groupId, taskPlans }) => {
            acc[groupId] = taskPlans;
            return acc;
        }, {} as Record<string, TaskPlanOptionType[]>);
    };

    const fetchFaultsForGroups = async (missionId: string, satelliteGroups: string[]): Promise<Record<string, FaultsOptionType[]>> => {
        if (!satelliteGroups?.length) return {};

        const groupPromises = satelliteGroups.map(async (groupId) => {
            try {
                const groupFaults = await getFaultsByGroupId(missionId, groupId);
                return { groupId: groupId, faults: groupFaults };
            } catch (err) {
                return { groupId: groupId, faults: [] };
            }
        });

        const results = await Promise.all(groupPromises);
        const validResults = results.filter(r => r !== null);

        return validResults.reduce((acc, { groupId, faults }) => {
            acc[groupId] = faults;
            return acc;
        }, {} as Record<string, FaultsOptionType[]>);
    };

    const getAllSatelliteGroups = async (missionId: string, scenario: ScenarioResponseType | null): Promise<{
        allGroups: GroupOptionType[];
        allTaskPlans: Record<string, TaskPlanOptionType[]>
        faults: Record<string, FaultsOptionType[]>
        allPayloads: Record<string, RemoteAppDataType[]>
        allSubsystems: Record<string, FaultSubsystemsType>
    }> => {
        const details: any = {
            allGroups: [],
            allTaskPlans: {},
            faults: {},
            allPayloads: {},
            allSubsystems: {}
        }
        try {
            const res = await SimulateService.getSatelliteGroups(missionId);
            if (!res?.data?.length) {
                return details
            }
            const groups: GroupOptionType[] = [];
            const scenarioGroups = scenario?.satelliteGroups || [];
            for (const [index, group] of res.data.entries()) {
                if (group?.draftMode) continue;
                groups.push({
                    groupId: group?.['uniqueId'],
                    groupType: group?.['groupType']?.['type'],
                    groupName: group?.["name"],
                    satCount: group?.["satCount"],
                    phasingType: group?.["phasingType"] || '',
                    phasingAngle: group?.["phasingAngle"] || 0,
                    separationAngleBetweenPlane: group?.['separationAngleBetweenPlane'] ?? group?.['groupProperties']?.['separationAngleBetweenPlane'],
                    draftMode: group?.["draftMode"],
                    noOfPlanes: group?.['noOfPlanes'] || 0,
                    noOfSatellitesPerPlane: group?.['noOfSatellitesPerPlane'] || 0,
                    orbitData: {
                        altitude: group?.['referenceOrbitParameters']?.['altitude'],
                        inclination: group?.['referenceOrbitParameters']?.['inclination'],
                        eccentricity: group?.['referenceOrbitParameters']?.['eccentricity'],
                        RAAN: group?.['referenceOrbitParameters']?.['RAAN'],
                        AP: group?.['referenceOrbitParameters']?.['AP'],
                        TA: group?.['referenceOrbitParameters']?.['TA'],
                        epoch: getValidEpoch(group?.['referenceOrbitParameters']?.['epoch'], scenarioStartTime)
                    },
                    color: getSatelliteColor(index),
                    satellites: group?.satellites || [],
                    thermal: group?.satConfig?.thermal
                });
            }
            details.allGroups = groups;
            // Skip task plans, faults, and payloads for TRUETWIN_COMMAND_CENTER as it doesn't use SCHEDULE and FAULTS steps
            if (type === 'simulation' && scenario?.scenarioDeploymentType !== SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER) {
                const [taskPlans, faults, allPayloads,] = await Promise.allSettled([
                    fetchTaskPlansForGroups(scenarioGroups.map(group => group.satelliteGroupId)),
                    fetchFaultsForGroups(missionId, scenarioGroups.map(group => group.satelliteGroupId)),
                    getAllPayloadsDetails(missionId)
                ]);
                details.allTaskPlans = taskPlans.status === "fulfilled" ? taskPlans.value : {}
                details.faults = faults.status === "fulfilled" ? faults.value : {}
                details.allPayloads = allPayloads.status === "fulfilled" ? allPayloads.value : {}
            }
            return details
        } catch (err) {
            return details
        }
    }

    const getScenarioDetails = (missionId: string, scenarioId: string): Promise<ScenarioResponseType> => {
        return new Promise((resolve, reject) => {
            SimulateService.getScenario(missionId, scenarioId)
                .then((res) => {
                    if (res?.data) {
                        setScenarioResponse(res.data)
                        resolve(res.data);
                    } else {
                        reject(new Error('Failed to fetch scenario details'));
                    }
                })
                .catch((error) => {
                    const message = getApiFailureMessage(error, 'Fetch');
                    reject(new Error(message));
                });
        });
    }

    const fetchBaseData = async (missionId: string, scenario: ScenarioResponseType | null) => {
        // Skip geo features API call for TRUETWIN_COMMAND_CENTER as it doesn't use task plans and fault plans (which use geo features)
        const isTrueTwinCommandCenter = scenario?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER || (scenario === null && type !== 'simulation');
        const [geo, stations, satGroups] = await Promise.allSettled([
            isTrueTwinCommandCenter ? Promise.resolve([]) : getAllGeoFeatures(missionId),
            getMissionDetails(missionId),
            getAllSatelliteGroups(missionId, scenario)
        ]);

        return {
            geofeatures: geo.status === "fulfilled" ? geo.value : [],
            stations: stations.status === "fulfilled" ? stations.value : [],
            satGroups: satGroups.status === "fulfilled" ? satGroups.value.allGroups : [],
            allTaskPlans: satGroups.status === "fulfilled" ? satGroups.value.allTaskPlans : {},
            allFaults: satGroups.status === "fulfilled" ? satGroups.value.faults : {},
            allPayloads: satGroups.status === "fulfilled" ? satGroups.value.allPayloads : {},
            allSubsystems: satGroups.status === "fulfilled" ? satGroups.value.allSubsystems : {}
        };
    };

    const fetchScheduleDetails = async (missionId: string, scenarioId: string): Promise<any[]> => {
        return new Promise(async (resolve) => {
            try {
                const response = await SimulateService.generateImportedSchedule(missionId, scenarioId);
                const scheduleData = response?.data || [];
                resolve(scheduleData);
            } catch (error: any) {
                resolve([]);
            }
        });
    }

    const fetchScenarioAssets = async (missionId: string, scenario: ScenarioResponseType) => {
        const assets: FormAssetType = {
            schedule: { status: 'idle', data: null, allSchedules: [] },
            faultPlans: { status: 'idle', data: null },
            hwilAssets: { status: 'idle', data: null },
            presim: { fidelityType: 'high', data: formatPresimData(null, scenario?.satelliteGroups || []) },
            orbitalInsights: { status: 'idle', data: null }
        };
        const scenarioId = scenario?.scenarioId;
        const setupStatus = getScenarioSetupStatus(scenario);
        if (isSatelliteGroupsAddRunning(setupStatus)) {
            const message = getScenarioSetupMessage(scenario);
            setLoading((prev) => ({
                ...prev,
                execution: 'Plotting',
                executionMessage: message || prev.executionMessage || false
            }));
            updateScenarioStep(SCENARIO_STEPS.SATELLITES, scenarioId);
            return assets;
        }
        if (setupStatus === SCENARIO_SETUP_STATUS.SATELLITE_GROUPS_ADD_FAILED) {
            const message = getScenarioSetupMessage(scenario);
            setLoading((prev) => ({ ...prev, execution: false, executionMessage: false }));
            if (message) showToastMessage('error', message);
            return assets;
        }
        if (setupStatus === SCENARIO_SETUP_STATUS.SATELLITE_GROUPS_ADD_COMPLETED) {
            setLoading((prev) => prev.execution === 'Plotting' ? { ...prev, execution: false, executionMessage: false } : prev);
        }
        const hasGroups = scenario?.satelliteGroups?.length > 0;
        if (!hasGroups) return assets;
        const groups = scenario?.satelliteGroups;
        const selectedGroup = groups?.[0];
        const selectedSatellite = selectedGroup?.satellites?.[0]?.satelliteId;
        await createSatellitePacket(viewer, scenarioId, missionId, selectedSatellite)
        onSelectGroup(selectedGroup);

        // Skip schedule, faults, presim, and orbital insights for TRUETWIN_COMMAND_CENTER as it doesn't use SCHEDULE and FAULTS steps
        const isTrueTwinCommandCenter = scenario.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER;

        if (!isTrueTwinCommandCenter) {
            const orbitalInsightsResponse = await getOrbitalInsights(missionId, scenarioId, false);
            const orbitalInsights = formatOrbitalInsightsData(orbitalInsightsResponse);
            assets.orbitalInsights = {
                status: orbitalInsights ? 'completed' : 'idle',
                data: orbitalInsights
            }
            // Faults
            const faultReport = await getallFaultReportData()
            assets.faultPlans = {
                status: scenario.draftMode === 'No' ? 'completed' : 'invalidated',
                data: faultReport.data
            }
            // Schedule
            const scheduleMessage = getScenarioSetupMessage(scenario);

            if (isOnboardScheduleGenerateRunning(setupStatus)) {
                assets.schedule = { status: 'running', data: null };
                setLoading((prev) => ({
                    ...prev,
                    execution: 'Generating',
                    executionMessage: scheduleMessage || prev.executionMessage || false
                }));
            } else if (setupStatus === SCENARIO_SETUP_STATUS.ONBOARD_SCHEDULE_GENERATE_FAILED) {
                // Allow user to retry generation
                assets.schedule = { status: 'invalidated', data: null };
                setLoading((prev) => prev.execution === 'Generating' ? { ...prev, execution: false, executionMessage: false } : prev);
                if (scheduleMessage) showToastMessage('error', scheduleMessage);
            } else if (
                scenario.draftMode === 'No' ||
                setupStatus === SCENARIO_SETUP_STATUS.ONBOARD_SCHEDULE_GENERATE_COMPLETED
            ) {
                const scheduleResp = await fetchScheduleDetails(missionId, scenario.scenarioId);
                const schedules = scenario.scenarioOperation === SCENARIO_OPERATION_TYPES.CONOPS ? getFormattedScheduleData(scheduleResp) : {}
                assets.schedule = {
                    status: 'completed',
                    data: schedules
                };
                const selectedGroup = scenario?.satelliteGroups[0]
                const selectedSatellite = selectedGroupSatellite?.satelliteId || selectedGroup?.satellites[0].satelliteId
                resetTimelineFilter(schedules, selectedSatellite)
                setLoading((prev) => prev.execution === 'Generating' ? { ...prev, execution: false, executionMessage: false } : prev);
            }
            if (scenario.draftMode === 'No') {
                // Presim
                const presimData = await getPresimData(missionId, scenario.scenarioId, scenario, assets.presim.data!);
                assets.presim.data = {
                    ...assets.presim.data,
                    ...presimData
                }
            }
        }

        // HWIL Assets
        if (scenario.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP) {
            const configGenerated = [SCENARIO_STATUS.HWIL_CONFIG_GENERATED, SCENARIO_STATUS.WAITING_FOR_CONNECTION].includes(scenario.status);
            assets.hwilAssets = {
                status: configGenerated ? 'completed' : assets.hwilAssets.status,
                data: configGenerated ? scenario.hwInfo : assets.hwilAssets.data
            };
            if (scenario.status === SCENARIO_STATUS.WAITING_FOR_CONNECTION) {
                setLoading((prev) => ({
                    ...prev,
                    execution: 'Connecting'
                }))
                await getRemoteAppConnectionStatus(false)
            }
        }
        return assets;
    };


    const mapScenarioSelection = (result: any, scenario: ScenarioResponseType) => {
        const selectedSatellites: FormGroupDetailsType[] = [];
        const selectedTaskPlans: SelectedTaskplansType[] = [];
        const selectedFaults: SelectedFaultType[] = [];

        (scenario.satelliteGroups || []).forEach(group => {
            const matchGroup = result?.satGroups.find(g => g?.groupId === group?.satelliteGroupId);
            const matchTaskPlan = result?.allTaskPlans[group?.satelliteGroupId]?.find(tp => tp?.taskPlanId === group?.taskPlanId);
            const matchedFaults = result?.allFaults[group?.satelliteGroupId]?.find(f => f?.faultPlanId === group?.faultPlanId);

            if (!matchGroup) {
                // Draft satellite — filtered out of allGroups but already linked to this scenario.
                // Still populate formData.satellites so validateSameScenarioSatellites sees them as
                // "saved" and doesn't treat SATELLITES as having unsaved changes, which would
                // incorrectly disable SEGMENTS and SCHEDULE via DisabledSteps().
                if (group?.draftMode) {
                    selectedSatellites.push({
                        groupName: group.satelliteGroupName,
                        groupId: group.satelliteGroupId,
                        satelliteId: scenario?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? group?.satellites?.[0]?.satelliteId : '',
                        satelliteName: scenario?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? group?.satellites?.[0]?.satelliteName : '',
                        satCount: group.satelliteCount || 1,
                        phasingType: group.satellitePhasingType || '',
                        phasingAngle: group.satellitePhasingAngle || 0,
                        noOfPlanes: 0,
                        noOfSatellitesPerPlane: 0,
                        referenceOrbit: group.groupReferenceOrbit,
                        color: group.color || ''
                    });
                }
                return;
            }

            selectedSatellites.push({
                groupName: matchGroup?.groupName,
                groupId: matchGroup?.groupId,
                satelliteId: scenario?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? group?.satellites?.[0]?.satelliteId : '',
                satelliteName: scenario?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? group?.satellites?.[0]?.satelliteName : '',
                satCount: matchGroup.satCount,
                phasingType: matchGroup?.phasingType,
                phasingAngle: group?.satellitePhasingAngle,
                noOfPlanes: matchGroup?.noOfPlanes,
                noOfSatellitesPerPlane: matchGroup?.noOfSatellitesPerPlane,
                referenceOrbit: group?.groupReferenceOrbit,
                color: group?.color
            });

            if (matchTaskPlan) selectedTaskPlans.push({
                satelliteGroupId: group?.satelliteGroupId,
                taskPlanId: group?.taskPlanId,
                taskPlanName: matchTaskPlan?.taskPlanName
            });

            if (matchedFaults) selectedFaults.push({
                satelliteGroupId: group?.satelliteGroupId,
                faultPlanId: group?.faultPlanId,
                faultPlanName: matchedFaults?.faultPlanName
            });
        });

        return { selectedSatellites, selectedTaskPlans, selectedFaults };
    };


    const getScenarioBaseData = async (missionId: string, scenarioId?: string | null) => {
        try {
            setSatelliteBaseDataLoaded(false);
            let allHwilComponents: AllHwilComponentType = {
                payload: [],
                bus: []
            }
            let scenario: ScenarioResponseType | null = null;
            let assets: FormAssetType = {
                schedule: { status: 'idle', data: null },
                faultPlans: { status: 'idle', data: null },
                hwilAssets: { status: 'idle', data: null },
                presim: { fidelityType: 'high', data: formatPresimData(null, []) },
                orbitalInsights: { status: 'idle', data: null }
            };

            setLoading(prev => ({ ...prev, base: true }));
            if (scenarioId) {
                scenario = await getScenarioDetails(missionId, scenarioId);
                assets = await fetchScenarioAssets(missionId, scenario);
                setScenarioResponse(scenario)
                if (scenario.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP) {
                    allHwilComponents = await getHWILComponents()
                }
            }
            const baseData = await fetchBaseData(missionId, scenario);
            const { selectedSatellites, selectedTaskPlans, selectedFaults } = scenario ? mapScenarioSelection({ ...baseData, scenario }, scenario) : { selectedSatellites: [], selectedTaskPlans: [], selectedFaults: [] };

            setFormData(prev => {
                let data: ScenarioFormDataType = {
                    ...prev,
                    details: scenario ? {
                        ...prev.details,
                        scenarioName: scenario.scenarioName,
                        scenarioStartTime: scenario.scenarioStartTime || prev.details.scenarioStartTime,
                        sampleRate: scenario.sampleRate || prev.details.sampleRate,
                        scenarioDeploymentType: scenario.scenarioDeploymentType || prev.details.scenarioDeploymentType,
                        scenarioOperation: scenario.scenarioOperation || prev.details.scenarioOperation,
                    } : prev.details,
                    satellites: selectedSatellites.length > 0 ? selectedSatellites : prev.satellites,
                    initial_altitude: {
                        angularRate: scenario?.angularRate || prev?.initial_altitude?.angularRate,
                        orientation: scenario?.orientation || prev?.initial_altitude?.orientation
                    },
                    segments: {
                        ...prev.segments,
                        stations: (scenario && scenario?.groundStations?.length > 0) ? scenario?.groundStations : baseData.stations.map((station) => station?.groundStationId)
                    },
                    hwil: selectedSatellites?.length > 0 && scenario?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? {
                        hwInfo: scenario?.hwInfo || [],
                        remoteAppConfiguration: {
                            ...scenario?.remoteAppConfiguration,
                            remoteApp: {
                                AppData: (scenario?.remoteAppConfiguration?.remoteApp?.AppData || []).map(app => {
                                    const matchedPayload = baseData.allPayloads[selectedSatellites?.[0].groupId]?.find(payload => payload.id === app.id)
                                    return matchedPayload ? { ...matchedPayload, ...app } : app
                                }) || []
                            }
                        }
                    } : prev.hwil,
                    schedule: {
                        scheduleType: 'custom',
                        taskplans: selectedTaskPlans,
                        importedSchedule: null,
                        simulationStartTime: scenario?.simulationStartTime || prev.schedule.simulationStartTime,
                        simulationDuration: scenario?.simulationDuration || prev.schedule.simulationDuration
                    },
                    faults: selectedFaults,
                    assets: { ...prev.assets, ...assets }
                }
                const { allGroundStations, selectedStations, selectedTaskplans, selectedFaultPlans } = getViewerPacketDetails(data, baseData.allTaskPlans, baseData.geofeatures, baseData.allFaults, baseData.stations)
                createGroundPacket(viewer, allGroundStations, selectedStations, selectedTaskplans, selectedFaultPlans);
                return data
            });
            setScenarioBaseData({
                allGeoFeatures: baseData.geofeatures,
                allGroups: baseData.satGroups,
                allStations: baseData.stations,
                allTaskPlans: baseData.allTaskPlans,
                allFaults: baseData.allFaults,
                allPayloads: baseData.allPayloads,
                allHwilComponents: allHwilComponents,
                allSubsystems: baseData.allSubsystems
            });
        } catch (err: any) {
            showToastMessage('error', err?.message || 'Something went wrong. Try again.');
        } finally {
            setLoading(prev => ({ ...prev, base: false }));
            setSatelliteBaseDataLoaded(true);
        }
    };


    return {
        getScenarioDetails,
        getScenarioBaseData,
        getTaskPlansByGroupId,
    }
}

export default useBaseApi
